import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { createProduct, fetchProducts } from '../../api/product';
import { Product } from '../../types/product';
import { notifyError } from '../../utils/notify';
import './CompanyInventoryPickerModal.css';

type Props = {
  open: boolean;
  products: Product[];
  title?: string;
  onClose: () => void;
  onSelect: (product: Product) => void;
  onListsUpdated?: (products: Product[]) => void;
};

function ProductPickerModal({
  open,
  products,
  title = '제품 선택',
  onClose,
  onSelect,
  onListsUpdated,
}: Props) {
  const [keyword, setKeyword] = useState('');
  const [selectingId, setSelectingId] = useState<number | null>(null);
  const [showQuick, setShowQuick] = useState(false);
  const [quickCode, setQuickCode] = useState('');
  const [quickName, setQuickName] = useState('');
  const [quickSpec, setQuickSpec] = useState('');
  const [quickUnit, setQuickUnit] = useState('EA');
  const [quickSaving, setQuickSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setKeyword('');
      setSelectingId(null);
      setShowQuick(false);
      setQuickCode('');
      setQuickName('');
      setQuickSpec('');
      setQuickUnit('EA');
      return;
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && selectingId == null && !quickSaving) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, selectingId, quickSaving]);

  const filtered = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    if (!q) return products;
    return products.filter((row) => {
      const hay = [row.productCode, row.productName, row.category, row.spec, row.unit, row.manufacturer]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [products, keyword]);

  const pickProduct = (product: Product) => {
    try {
      setSelectingId(product.productId);
      onSelect(product);
      onClose();
    } finally {
      setSelectingId(null);
    }
  };

  const handleQuickCreate = async () => {
    if (!quickName.trim()) {
      notifyError('제품명을 입력해 주세요.');
      return;
    }
    try {
      setQuickSaving(true);
      const created = await createProduct({
        productCode: quickCode.trim() || undefined,
        productName: quickName.trim(),
        spec: quickSpec.trim() || undefined,
        unit: quickUnit.trim() || undefined,
        useYn: 'Y',
      });
      const next = await fetchProducts();
      const active = next.filter((p) => (p.useYn || 'Y') !== 'N');
      onListsUpdated?.(active);
      const resolved = active.find((p) => p.productId === created.productId) ?? created;
      onSelect(resolved);
      onClose();
    } catch (err) {
      notifyError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '제품 등록에 실패했습니다.'
      );
    } finally {
      setQuickSaving(false);
    }
  };

  if (!open) return null;

  const busy = selectingId != null || quickSaving;

  return createPortal(
    <div
      className="inv-picker-modal__backdrop"
      role="presentation"
      onClick={() => {
        if (!busy) onClose();
      }}
    >
      <div
        className="inv-picker-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="product-picker-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="inv-picker-modal__head">
          <div>
            <p className="inv-picker-modal__eyebrow">제품 마스터</p>
            <h3 id="product-picker-modal-title">{title}</h3>
            <p className="inv-picker-modal__sub">
              제품코드·제품명으로 검색해 선택하세요. 없으면 빠른 등록으로 바로 추가할 수 있습니다.
            </p>
          </div>
          <button
            type="button"
            className="inv-picker-modal__close"
            aria-label="닫기"
            disabled={busy}
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="inv-picker-modal__toolbar">
          <input
            type="search"
            className="inv-picker-modal__search"
            placeholder="제품코드·제품명·규격·카테고리 검색"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            disabled={busy}
            autoFocus
          />
          <span className="inv-picker-modal__count">
            {filtered.length}/{products.length}건
          </span>
          <button
            type="button"
            className="inv-picker-modal__quick-toggle"
            disabled={busy}
            onClick={() => setShowQuick((v) => !v)}
          >
            {showQuick ? '빠른 등록 닫기' : '빠른 등록'}
          </button>
        </div>

        {showQuick ? (
          <div className="inv-picker-modal__quick">
            <input
              placeholder="제품코드"
              value={quickCode}
              disabled={busy}
              onChange={(e) => setQuickCode(e.target.value)}
            />
            <input
              placeholder="제품명 *"
              value={quickName}
              disabled={busy}
              onChange={(e) => setQuickName(e.target.value)}
            />
            <input
              placeholder="규격"
              value={quickSpec}
              disabled={busy}
              onChange={(e) => setQuickSpec(e.target.value)}
            />
            <input
              placeholder="단위"
              value={quickUnit}
              disabled={busy}
              onChange={(e) => setQuickUnit(e.target.value)}
            />
            <button
              type="button"
              className="inv-picker-modal__quick-submit"
              disabled={busy}
              onClick={() => void handleQuickCreate()}
            >
              {quickSaving ? '등록 중…' : '등록 후 선택'}
            </button>
          </div>
        ) : null}

        <div className="inv-picker-modal__body">
          {products.length === 0 ? (
            <p className="inv-picker-modal__empty">
              등록된 제품이 없습니다. 「빠른 등록」으로 추가하세요.
            </p>
          ) : filtered.length === 0 ? (
            <p className="inv-picker-modal__empty">검색 결과가 없습니다.</p>
          ) : (
            <div className="inv-picker-modal__table-wrap">
              <table className="inv-picker-modal__table">
                <thead>
                  <tr>
                    <th style={{ width: 120 }}>제품코드</th>
                    <th>제품명</th>
                    <th style={{ width: 120 }}>카테고리</th>
                    <th>규격</th>
                    <th style={{ width: 64 }}>단위</th>
                    <th style={{ width: 88 }} />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row) => (
                    <tr
                      key={row.productId}
                      className={selectingId === row.productId ? 'is-busy' : undefined}
                      onClick={() => {
                        if (!busy) pickProduct(row);
                      }}
                    >
                      <td>{row.productCode?.trim() || '-'}</td>
                      <td>{row.productName || '-'}</td>
                      <td>{row.category?.trim() || '-'}</td>
                      <td>{row.spec?.trim() || '-'}</td>
                      <td>{row.unit?.trim() || '-'}</td>
                      <td>
                        <button
                          type="button"
                          className="inv-picker-modal__pick"
                          disabled={busy}
                          onClick={(e) => {
                            e.stopPropagation();
                            pickProduct(row);
                          }}
                        >
                          {selectingId === row.productId ? '선택 중…' : '선택'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default ProductPickerModal;
